import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import { baseApiUrl } from "~constants/baseApiUrl";
import type { RequestConfig } from "~types/requestConfig.ts";
import type { RequestErrorHandler } from "~types/requestErrorHandler.ts";
import { request } from "~utils/request";
import { useNotification } from "../../useNotification.ts";
import type { NotificationInstance } from "antd/es/notification/interface";
import { useGetLocationParams } from "~hooks/useGetLocationParams.ts";

type ErrorResponseData = { error: { message: string } } | { error: string } | { errors: { message: string } };

const showMessage = (message: string, notification: NotificationInstance, description?: string) =>
  notification.error({ message, description, placement: "bottomRight" });

export const useApi = () => {
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const { customerId } = useGetLocationParams();
  const { loginWithRedirect, logout } = useAuth0();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useCallback(
    async <TReturnValue>(endpoint: string, config: RequestConfig<TReturnValue> = { method: "GET" }) => {
      const [idTokenPromise, accessTokenPromise] = await Promise.allSettled([getIdTokenClaims(), getAccessTokenSilently()]);
      if (idTokenPromise.status === "rejected" || accessTokenPromise.status === "rejected") {
        await logout({ logoutParams: { returnTo: window.location.origin } });
      }
      const idToken = idTokenPromise.status === "fulfilled" ? idTokenPromise.value?.__raw : "";
      const accessToken = accessTokenPromise.status === "fulfilled" ? accessTokenPromise.value : "";

      const getErrorMessage = (data: ErrorResponseData) => {
        const errorMessage =
          typeof (data as { error: string })?.error === "string"
            ? (data as { error: string }).error
            : (data as { error: { message: string } })?.error?.message ||
              (data as { errors: { message: string } })?.errors?.message;
        return String(errorMessage || formatMessage({ id: "errorMessages.response.default.unexpectedError" }));
      };

      const defaultErrorHandler: RequestErrorHandler = {
        400: (_: Response, data: unknown) => showMessage(getErrorMessage(data as ErrorResponseData), notification),
        401: () => loginWithRedirect(),
        403: () => showMessage(formatMessage({ id: "errorMessages.response.403" }), notification),
        404: () => showMessage(formatMessage({ id: "errorMessages.response.404" }), notification),
        422: (_: Response, data: unknown) => showMessage(getErrorMessage(data as ErrorResponseData), notification),
        default: (_: Response, data: unknown) =>
          showMessage(
            formatMessage({ id: "errorMessages.response.default" }),
            notification,
            getErrorMessage(data as ErrorResponseData),
          ),
      };

      const onErrorHandler = config.onError
        ? typeof config.onError === "function"
          ? config.onError
          : { ...defaultErrorHandler, ...config.onError }
        : defaultErrorHandler;

      const extendedConfig: RequestConfig<TReturnValue> = {
        ...config,
        onError: onErrorHandler,
        headers: {
          ...config.headers,
          "x-user-identity-token": accessToken,
          ...(idToken && { Authorization: `Bearer ${idToken}` }),
          ...(customerId && { "x-customer-id": customerId }),
        },
      };

      return request<TReturnValue>(endpoint.startsWith("http") ? endpoint : `${baseApiUrl}${endpoint}`, extendedConfig);
    },
    [getAccessTokenSilently, getIdTokenClaims, customerId, loginWithRedirect, formatMessage, notification],
  );
};
